import React from 'react';
import { Link } from 'react-router-dom';

interface PageHeaderProps {
  /** Page title — must be the EXACT sidebar-nav label (product rule 2026-07-20:
   *  page titles mirror the navbar wording, with no icon beside them). */
  title: string;
  /** Hide the "Dashboard • {title}" path line (used on the Dashboard itself). */
  noPath?: boolean;
  /** Path root — defaults to the management Dashboard; the learner surface
   *  passes { label: 'Home', to: '/learn/dashboard' }. */
  root?: { label: string; to: string };
  /** Intermediate path segments between the root and the current page, so the
   *  path shows the precise reality (e.g. Dashboard • Settings • Team).
   *  Segments with `to` render as links. */
  trail?: Array<{ label: string; to?: string }>;
  /** Last path segment — defaults to `title`. Use when the current location is
   *  deeper than the page itself (e.g. an active tab or an entity name). */
  leaf?: string;
  /** Right-aligned header actions (buttons, filters). */
  actions?: React.ReactNode;
  className?: string;
}

const DEFAULT_ROOT = { label: 'Dashboard', to: '/dashboard' };

/**
 * Standard management-page header: the navbar label as the title and the
 * "Dashboard • Page" path underneath. One component so titles, the path line,
 * and the brand purple (light rgb(var(--color-primary)) / dark #9C7CE8) can never drift per page.
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  noPath = false,
  root = DEFAULT_ROOT,
  trail = [],
  leaf,
  actions,
  className,
}) => (
  <div className={`flex items-start justify-between gap-4 ${className ?? ''}`}>
    <div>
      <h1 className="text-2xl font-bold text-text-primary">{title}</h1>
      {!noPath && (
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 mt-1">
          <Link
            to={root.to}
            className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
          >
            {root.label}
          </Link>
          {trail.map((seg) => (
            <React.Fragment key={seg.label}>
              <span className="body-small text-text-secondary">•</span>
              {seg.to ? (
                <Link
                  to={seg.to}
                  className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
                >
                  {seg.label}
                </Link>
              ) : (
                <span className="body-small text-text-secondary">{seg.label}</span>
              )}
            </React.Fragment>
          ))}
          <span className="body-small text-text-secondary">•</span>
          <span className="body-small text-primary-600 dark:text-primary-350 font-medium">
            {leaf ?? title}
          </span>
        </nav>
      )}
    </div>
    {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
  </div>
);

export default PageHeader;
